/**
 * MiniPlayer.tsx
 * =============================================================================
 * Compact floating player that appears when user navigates away from home.
 * Allows control of audio session without returning to main player.
 *
 * Design: Dark glassmorphism with essential controls only.
 *
 * @author Neuro-Audio Lab
 * @version 1.0.0
 */

import { useCallback } from 'react';
import { Play, Pause, Square } from 'lucide-react';
import { useAudioEngine, useTimer } from '../../hooks/useAudioEngine';
import { getAudioEngine } from '../../core/AudioEngine';
import { MODE_CONFIGS } from '../../config/audioPresets';
import { TIMER_PROFILES } from '../../core/types';

export function MiniPlayer() {
  const {
    isPlaying,
    isPaused,
    isLoading,
    currentMode,
    pause,
    resume,
    stop,
  } = useAudioEngine();

  const {
    currentTimerProfile,
    formattedSession,
    formattedRemaining,
    pauseTimer,
    startTimer,
    resetTimer,
  } = useTimer();

  // Mode config for styling
  const modeConfig = currentMode ? MODE_CONFIGS[currentMode] : MODE_CONFIGS.focus;

  // Play/Pause handler
  const handlePlayPause = useCallback(() => {
    // [v3.6] MOBILE FIX: Synchronous AudioContext creation + resume
    // Must be the first line — before any async ops or state reads
    getAudioEngine().ensureContext();

    if (isPlaying && !isPaused) {
      pause();
      pauseTimer();
    } else if (isPaused) {
      resume();
      startTimer();
    }
  }, [isPlaying, isPaused, pause, resume, pauseTimer, startTimer]);

  // Stop handler
  const handleStop = useCallback(() => {
    stop();
    resetTimer();
  }, [stop, resetTimer]);

  const isStopDisabled = !isPlaying && !isPaused;
  const timerDisplay = currentTimerProfile === 'free' ? formattedSession : formattedRemaining;

  return (
    <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-4 duration-300">
      {/* Main Container - Glassmorphism */}
      <div
        className="backdrop-blur-md rounded-2xl border shadow-2xl overflow-hidden"
        style={{
          background: 'rgba(0, 0, 0, 0.75)',
          borderColor: `${modeConfig.color}30`,
          boxShadow: `0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px ${modeConfig.color}20`,
        }}
      >
        {/* Top Bar - Mode Name */}
        <div
          className="px-4 py-2 border-b"
          style={{
            background: `linear-gradient(135deg, ${modeConfig.color}15, ${modeConfig.color}05)`,
            borderBottomColor: `${modeConfig.color}20`,
          }}
        >
          <p className="text-xs font-medium text-neutral-200">
            {modeConfig.name}
          </p>
        </div>

        {/* Main Content - Timer + Controls */}
        <div className="px-4 py-3 flex items-center gap-4">
          {/* Timer Display */}
          <div className="flex flex-col items-start min-w-[100px]">
            <span className="text-[9px] text-neutral-500 uppercase tracking-wider mb-0.5">
              {currentTimerProfile === 'free' ? 'Sesión' : TIMER_PROFILES[currentTimerProfile].name}
            </span>
            <span className="text-xl font-mono font-bold text-neutral-200 tabular-nums">
              {timerDisplay}
            </span>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2">
            {/* Play/Pause Button */}
            <button
              onClick={handlePlayPause}
              disabled={isLoading}
              className={`
                w-10 h-10 rounded-full flex items-center justify-center
                backdrop-blur-sm border transition-all duration-200
                ${isLoading
                  ? 'opacity-50 cursor-wait bg-white/5 border-white/10'
                  : 'hover:scale-110 active:scale-95 bg-white/10 border-white/20 hover:bg-white/15'
                }
              `}
              aria-label={isPlaying && !isPaused ? 'Pausar' : 'Reproducir'}
            >
              {isLoading ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : isPlaying && !isPaused ? (
                <Pause className="w-5 h-5 text-white" />
              ) : (
                <Play className="w-5 h-5 text-white ml-0.5" />
              )}
            </button>

            {/* Stop Button */}
            <button
              onClick={handleStop}
              disabled={isStopDisabled}
              className={`
                w-8 h-8 rounded-full flex items-center justify-center
                backdrop-blur-sm border transition-all duration-200
                ${
                  !isStopDisabled
                    ? 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20 text-neutral-300 hover:text-neutral-100'
                    : 'bg-white/[0.02] border-white/5 text-neutral-600 cursor-not-allowed'
                }
              `}
              aria-label="Detener"
            >
              <Square className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Glow Effect */}
      <div
        className="absolute inset-0 rounded-2xl -z-10 blur-xl opacity-30"
        style={{
          background: `radial-gradient(circle at center, ${modeConfig.color}, transparent 70%)`,
        }}
      />
    </div>
  );
}

export default MiniPlayer;
